import { z } from 'zod';
import type { SourceFact } from './assistant';
import { propertySearchSchema, type PropertySearch } from './catalog';
import type { PropertyCatalogResult } from './catalog-results';

const field = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,79}$/u);
export const catalogAttributeSchema = z
  .object({
    key: field,
    label: z.string().trim().min(1).max(100),
    type: z.enum([
      'text',
      'number',
      'decimal',
      'boolean',
      'text_list',
      'money',
    ]),
    unit: z.string().trim().min(1).max(20).nullable(),
  })
  .strict();
export const catalogConfigurationSchema = z
  .object({
    profile: z.enum(['property', 'general']),
    label: z.string().trim().min(1).max(80),
    referenceTerms: z.array(z.string().trim().min(1).max(40)).max(12),
    attributes: z.array(catalogAttributeSchema).max(30),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.attributes.map((item) => item.key)).size ===
      value.attributes.length,
    'Catalog attribute keys must be unique.'
  );
export type CatalogConfiguration = z.infer<typeof catalogConfigurationSchema>;
export type CatalogAttribute = z.infer<typeof catalogAttributeSchema>;
export const catalogReferenceSchema = z
  .object({
    kind: z.enum(['id', 'code']),
    value: z.string().trim().min(1).max(160),
  })
  .strict();
export type CatalogReference = z.infer<typeof catalogReferenceSchema>;
// One reference read is one provider search with one alternative per reference.
export const CATALOG_READ_REFERENCE_LIMIT = 5;
export const catalogSelectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('search') }).strict(),
  z
    .object({
      kind: z.literal('references'),
      references: z
        .array(catalogReferenceSchema)
        .min(1)
        .max(CATALOG_READ_REFERENCE_LIMIT),
    })
    .strict(),
]);
export const catalogQuerySchema = z
  .object({
    selection: catalogSelectionSchema,
    inventoryScope: z.enum(['project', 'placement']).nullable().optional(),
    requestedFields: z.array(field).max(24),
    questionIndexes: z.array(z.number().int().min(0).max(7)).max(8),
  })
  .strict();
export type CatalogQuery = z.infer<typeof catalogQuerySchema>;
// A question may name more products than one read holds. The catalog engine
// reads them in parts, each within the read limit.
export const CATALOG_QUESTION_REFERENCE_LIMIT = 10;
// The products and fields a question needs, separately from the reads that
// supply them.
export const catalogRequirementSchema = catalogQuerySchema
  .omit({ questionIndexes: true })
  .extend({
    selection: z.discriminatedUnion('kind', [
      catalogSelectionSchema.options[0],
      catalogSelectionSchema.options[1].extend({
        references: z
          .array(catalogReferenceSchema)
          .min(1)
          .max(CATALOG_QUESTION_REFERENCE_LIMIT),
      }),
    ]),
  });
export type CatalogRequirement = z.infer<typeof catalogRequirementSchema>;

export const catalogValueSchema = z.union([
  z.string().max(8000),
  z.number().finite(),
  z.boolean(),
  z.array(z.string().max(500)).max(80),
  z.null(),
]);
export const catalogItemSchema = z
  .object({
    id: z.string().min(1).max(160),
    code: z.string().min(1).max(160),
    title: z.string().min(1).max(500),
    attributes: z.record(field, catalogValueSchema),
  })
  .strict();
export type CatalogItem = z.infer<typeof catalogItemSchema>;
// General product adapters supply complete page metadata, never an inferred total.
export const catalogPageSchema = z
  .object({
    observedAt: z.iso.datetime({ offset: true }),
    items: z.array(catalogItemSchema).max(30),
    hasMore: z.boolean(),
    total: z.number().int().nonnegative().nullable(),
  })
  .strict();
export const catalogReadResultSchema = z
  .object({
    query: catalogQuerySchema,
    appliedPropertySearch: propertySearchSchema.optional(),
    profile: z.enum(['property', 'general']),
    status: z.enum([
      'matches',
      'no_matches',
      'partial',
      'not_found',
      'ambiguous',
      'unsupported',
      'unavailable',
    ]),
    items: z.array(catalogItemSchema).max(30),
    resolutions: z
      .array(
        z
          .object({
            reference: catalogReferenceSchema,
            status: z.enum([
              'resolved',
              'not_found',
              'ambiguous',
              'incomplete',
            ]),
            itemIds: z.array(z.string()).max(30),
            candidates: z
              .array(
                catalogItemSchema.pick({ id: true, code: true, title: true })
              )
              .max(30),
          })
          .strict()
      )
      .max(5),
    unknowns: z
      .array(z.object({ itemId: z.string(), field }).strict())
      .max(720),
    unsupportedFields: z.array(field).max(24),
    unverifiedCriteria: z.array(z.string().min(1).max(500)).max(20),
    observedAt: z.iso.datetime({ offset: true }).nullable(),
    hasMore: z.boolean().nullable(),
    total: z.number().int().nonnegative().nullable(),
    ordering: z
      .object({
        field,
        direction: z.enum(['ascending', 'descending']),
        scope: z.literal('returned_results'),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type CatalogReadResult = z.infer<typeof catalogReadResultSchema>;

export interface CatalogSourceRead {
  query: string;
  selection: CatalogQuery['selection'];
  propertySearch: PropertySearch | null;
}
export interface CatalogSourceResult {
  status: 'matches' | 'no_matches' | 'unsupported_criteria';
  facts: SourceFact[];
  projectFacts?: SourceFact[] | undefined;
  propertyCatalog?: PropertyCatalogResult;
  catalog?: z.infer<typeof catalogPageSchema> | undefined;
}
export interface CatalogPort {
  kind: 'property' | 'general';
  read(request: CatalogSourceRead): Promise<CatalogSourceResult>;
}
export interface CatalogExecution {
  result: CatalogReadResult;
  facts: SourceFact[];
  projectFacts: SourceFact[];
  propertyCatalog?: PropertyCatalogResult;
}
